import { LandingNav } from "@/components/landing/LandingNav";
import { SEO } from "@/components/SEO";
import { LandingFooter } from "@/components/landing/LandingFooter";

const Terms = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Terms of Service — RaffleKit"
                description="RaffleKit terms of service."
                canonical="https://rafflekit.app/terms"
                noIndex={true}
            />
            <LandingNav />

            <section className="pt-32 pb-20 bg-gray-50">
                <div className="container mx-auto px-4 max-w-4xl text-left">
                    <h1 className="font-heading text-4xl font-bold text-gray-900 mb-6">Terms of Service</h1>
                    <p className="text-gray-500 mb-12">Last updated: {new Date().toLocaleDateString()}</p>

                    <div className="prose prose-lg prose-gray max-w-none space-y-8">

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Company Identity</h2>
                            <p>
                                RaffleKit is an operating name of GKNS Holdings Inc., a California corporation ("RaffleKit," "Company," "we," "us," or "our"). These Terms of Service govern your access to and use of the RaffleKit website, applications, contest pages, and related services (collectively, the “Service”).
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Acceptance of Terms</h2>
                            <p>
                                By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, you may not use the Service. If you are using the Service on behalf of an organization, you represent that you have authority to bind that organization to these Terms.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Description of Service</h2>
                            <p>
                                RaffleKit provides software tools that allow users to create, manage, and administer promotional contests, giveaways, and sweepstakes. RaffleKit is a technology platform only and is not the sponsor, administrator, or guarantor of any contest created using the Service.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Platform Role & Sponsor Responsibility</h2>
                            <p>
                                RaffleKit does not sponsor, administer, or fulfill prizes for any contest. The user creating a contest (“Sponsor”) is solely responsible for:
                            </p>
                            <ul>
                                <li>Drafting and publishing official rules</li>
                                <li>Ensuring compliance with all applicable laws</li>
                                <li>Registering or bonding contests where required (e.g., NY/FL)</li>
                                <li>Prize fulfillment and tax reporting</li>
                                <li>Participant communications</li>
                            </ul>
                            <p>
                                RaffleKit disclaims all responsibility for Sponsor compliance failures or prize disputes.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Account Requirements</h2>
                            <p>
                                You must be at least 18 years old to use the Service. You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Subscription & Billing</h2>
                            <p>
                                Certain features require a paid subscription. Subscriptions renew automatically unless canceled prior to renewal. All fees are non-refundable except where required by law. We reserve the right to modify pricing with reasonable notice.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Free Tier Limitations</h2>
                            <p>
                                Free plans are subject to usage limits, including limits on active contests and total entries. We may modify plan features or limits at any time.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Sweepstakes & Legal Compliance</h2>
                            <p>
                                You agree not to use the Service to conduct illegal lotteries or unlawful promotions. You are solely responsible for ensuring that your contest complies with all applicable federal, state, and local laws, including but not limited to sweepstakes, lottery, tax, advertising, and consumer protection laws.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Winner Selection</h2>
                            <p>
                                Winner selection is based on eligible entries. Each valid entry represents one chance to win. Participants with multiple entries receive proportionally increased odds. Sponsors acknowledge and accept this weighted probability structure.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Acceptable Use</h2>
                            <p>
                                You may not use the Service to:
                            </p>
                            <ul>
                                <li>Conduct unlawful or deceptive promotions</li>
                                <li>Misrepresent prizes</li>
                                <li>Infringe intellectual property rights</li>
                                <li>Attempt to disrupt or abuse the platform</li>
                                <li>Manipulate referral or entry systems fraudulently</li>
                            </ul>
                            <p>
                                We may suspend or terminate accounts that violate these Terms.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Intellectual Property</h2>
                            <p>
                                The Service, including all software, design, branding, and functionality, is the exclusive property of GKNS Holdings Inc. Sponsors retain ownership of their submitted contest content.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Data & Privacy</h2>
                            <p>
                                Your use of the Service is governed by our Privacy Policy. Sponsors represent that they have obtained all necessary consents from participants and have a lawful basis for collecting and processing participant data.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Indemnification</h2>
                            <p>
                                You agree to indemnify and hold harmless GKNS Holdings Inc., its officers, employees, and agents from any claims, liabilities, damages, or expenses arising out of:
                            </p>
                            <ul>
                                <li>Your contests</li>
                                <li>Prize disputes</li>
                                <li>Legal or regulatory violations</li>
                                <li>Marketing representations</li>
                                <li>Participant complaints</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Disclaimer of Warranties</h2>
                            <p>
                                The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, express or implied, including warranties of merchantability or fitness for a particular purpose.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Limitation of Liability</h2>
                            <p>
                                To the maximum extent permitted by law, our total liability shall not exceed the greater of (a) $100 or (b) the amount you paid to us in the twelve (12) months preceding the claim. We are not liable for indirect, incidental, consequential, or punitive damages.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Arbitration & Governing Law</h2>
                            <p>
                                Any dispute shall be resolved by binding arbitration in California under applicable arbitration rules. You waive the right to a jury trial and participation in class actions. These Terms are governed by the laws of the State of California.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Changes to Terms</h2>
                            <p>
                                We may update these Terms at any time. Continued use of the Service constitutes acceptance of the revised Terms.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">18. Contact Information</h2>
                            <p>
                                RaffleKit (Operating name of GKNS Holdings Inc.)<br />
                                1968 S. Coast Hwy, Suite 3200, Laguna Beach, CA 92651, United States<br />
                                hello@rafflekit.app
                            </p>
                        </div>

                    </div>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};

export default Terms;